import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { loginUser } from "../redux/userReducer";
import api from "../api/api";

function getLoginErrorMessage(error) {
  const responseMessage = error?.response?.data?.message;

  if (typeof responseMessage === "string") {
    if (responseMessage === "User is not approved by admin.") {
      return "Your account is pending admin approval. Please try again later or contact the administrator.";
    }

    return responseMessage;
  }

  if (typeof error?.message === "string") {
    try {
      const parsed = JSON.parse(error.message);

      if (parsed?.message === "User is not approved by admin.") {
        return "Your account is pending admin approval. Please try again later or contact the administrator.";
      }

      if (typeof parsed?.message === "string") {
        return parsed.message;
      }
    } catch {
      if (error.message === "User is not approved by admin.") {
        return "Your account is pending admin approval. Please try again later or contact the administrator.";
      }

      return error.message;
    }
  }

  return "Unable to log in right now.";
}

function Login() {
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleTogglePassword = () => setShowPassword((prev) => !prev);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await api.login({
        Email: email,
        PasswordHash: password,
      });
      const payload = response?.data || response || {};
      const user =
        payload.user ||
        payload.data?.user ||
        payload.result?.user ||
        payload.User ||
        {};
      const token =
        payload.token ||
        payload.accessToken ||
        payload.jwtToken ||
        payload.Token ||
        payload.AccessToken ||
        payload.data?.token ||
        payload.data?.accessToken ||
        payload.data?.Token ||
        payload.data?.AccessToken ||
        payload.result?.token ||
        payload.result?.accessToken ||
        payload.result?.Token ||
        payload.result?.AccessToken ||
        user.token ||
        user.accessToken ||
        user.jwtToken ||
        user.Token ||
        user.AccessToken;

      dispatch(
        loginUser({
          ...user,
          ...(token ? { token } : {}),
          email: user.email || email,
        }),
      );
    } catch (err) {
      setError(getLoginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, #f3f7ff 0%, #eef4ff 50%, #f9fbff 100%)",
        px: { xs: 2, sm: 3 },
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(15, 23, 42, 0.12)",
            border: "1px solid rgba(148, 163, 184, 0.18)",
          }}
        >
          <Grid container>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  height: "100%",
                  background:
                    "linear-gradient(145deg, #0f172a 0%, #1e3a8a 100%)",
                  color: "white",
                  p: { xs: 4, sm: 6, md: 7 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Stack spacing={2.5}>
                  <Box
                    component="img"
                    src="/images/seeds_logo.png"
                    alt="Einstein Seeds logo"
                    sx={{
                      width: 92,
                      height: 92,
                      borderRadius: 3,
                      objectFit: "contain",
                      bgcolor: "white",
                      p: 1,
                    }}
                  />
                  <Typography variant="h4" fontWeight={700}>
                    Welcome back
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: "rgba(255,255,255,0.82)", maxWidth: 360 }}
                  >
                    Every login is a step back to the place where friendships
                    began and dreams took shape. Reconnect, collaborate, and
                    grow together with the Einstein Family
                  </Typography>
                </Stack>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  p: { xs: 4, sm: 5, md: 6 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{ color: "#0f172a", mb: 1 }}
                >
                  Sign in to your account
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Use your email and password to continue.
                </Typography>

                <Stack spacing={2.2} component="form" onSubmit={handleSubmit}>
                  {error ? <Alert severity="error">{error}</Alert> : null}
                  <TextField
                    label="Email address"
                    type="email"
                    fullWidth
                    placeholder="you@example.com"
                    variant="outlined"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                  <TextField
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    placeholder="Enter your password"
                    variant="outlined"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleTogglePassword}
                              edge="end"
                              aria-label="toggle password visibility"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <FormControlLabel
                      control={<Checkbox defaultChecked />}
                      label="Remember me"
                    />
                    <Link href="#" underline="hover" sx={{ fontWeight: 600 }}>
                      Forgot password?
                    </Link>
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      py: 1.2,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    {loading ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CircularProgress size={18} color="inherit" />
                        Signing in...
                      </Box>
                    ) : (
                      "Sign In"
                    )}
                  </Button>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                  >
                    New here?{" "}
                    <Link
                      component={RouterLink}
                      to="/register"
                      underline="hover"
                      sx={{ fontWeight: 600 }}
                    >
                      Create an account
                    </Link>
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}

export default Login;
